import { useEffect } from 'react';

import { listPlaylists } from './graphql/queries';
import {
  deleteMemberPlaylists,
  transferUserPlaylists,
} from './graphql/mutations';

export default function usePlaylists({
  useGraphQLMutation,
  useLazyGraphQLQuery,
  organizationId,
  registeredMembers,
  setModifyContentStatus,
}) {
  const limitPlaylists = 100;

  const [listPlaylistsQuery, listPlaylistsResponse] =
    useLazyGraphQLQuery(listPlaylists);

  const [deleteMemberPlaylistsMutation] = useGraphQLMutation(
    deleteMemberPlaylists,
    {
      update(cache, { data: { deleteMemberPlaylists } }) {
        deleteMemberPlaylists.forEach((deletedPlaylist) => {
          cache.evict({ id: cache.identify(deletedPlaylist) });
        });
        cache.gc();
      },
      onCompleted: () => {
        setModifyContentStatus((currentStatus) => ({
          ...currentStatus,
          playlists: true,
        }));
      },
      onError: () => {
        setModifyContentStatus((currentStatus) => ({
          ...currentStatus,
          error: true,
        }));
      },
    }
  );

  const [transferUserPlaylistsMutation] = useGraphQLMutation(
    transferUserPlaylists,
    {
      onCompleted: () => {
        setModifyContentStatus((currentStatus) => ({
          ...currentStatus,
          playlists: true,
        }));
      },
      onError: () => {
        setModifyContentStatus((currentStatus) => ({
          ...currentStatus,
          error: true,
        }));
      },
    }
  );

  const listPlaylistsHandler = () => {
    const filter = {
      administrativeView: true,
      organizationId,
      status: ['DRAFT', 'PUBLISHED', 'PAUSED', 'TEMPLATE'],
    };

    listPlaylistsQuery({
      variables: {
        filter,
        pagination: {
          limit: limitPlaylists,
          offset: 0,
        },
      },
    });
  };

  const requestToDeleteMemberPlaylists = (memberId) => {
    deleteMemberPlaylistsMutation({ variables: { memberId } });
  };

  const requestToTransferUserPlaylists = (values) => {
    const { memberId, contentTransferTo: newOwnerId } = values;

    const currentOwnerId = registeredMembers.find(
      (member) => member.id === memberId
    ).user.id;

    transferUserPlaylistsMutation({
      variables: {
        input: {
          currentOwnerId,
          newOwnerId,
        },
      },
    });
  };

  useEffect(() => {
    const { data, loading } = listPlaylistsResponse;

    if (data && !loading) {
      if (
        data.listPlaylists &&
        data.listPlaylists.length > 0 &&
        data.listPlaylists.length % limitPlaylists === 0
      ) {
        listPlaylistsResponse.fetchMore({
          variables: {
            pagination: {
              limit: limitPlaylists,
              offset: data.listPlaylists.length,
            },
          },
        });
      }
    }
  }, [listPlaylistsResponse.data, listPlaylistsResponse.loading]);

  useEffect(() => {
    listPlaylistsHandler();
  }, [organizationId]);

  return {
    loadingPlaylists: listPlaylistsResponse.loading,
    playlists: listPlaylistsResponse?.data?.listPlaylists || [],
    requestToDeleteMemberPlaylists,
    requestToTransferUserPlaylists,
  };
}
